import type { ConvenienceFeeItem } from '@b-stock/fe-core/bstock-next/components'
import type { PayMethod } from '@b-stock/payments-methods-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'

import type { CheckoutValues } from '../CheckoutValues'
import type { NetTermsPayMethod } from '../shared'
import { PAY_WITH_TERMS } from '../shared'
import {
  calculateNetTermsAmountUsed,
  canPayWithNetTerms,
  getSecondaryPayMethod,
  isCreditCardPaymentMethod,
  isNetTermsCoveredBySecondary,
} from './utils'

/**
 * Calculate convenience fee for net terms payment
 * Formula: 1.63% of balance method amount + $2.00 (200 cents)
 *
 * @param balanceMethodAmount - The amount covered by net terms (availableCreditInCents) in cents
 * @returns The convenience fee amount in cents
 */
export const calculateConvenienceFee = (
  balanceMethodAmount: number
): number => {
  if (balanceMethodAmount <= 0) return 0
  return Math.round(0.0163 * balanceMethodAmount + 200)
}

/**
 * Default credit card convenience fee multiplier (3.5%). Used when no
 * `multiplier` is supplied to `calculateCreditCardConvenienceFee`, preserving
 * historical behavior for call sites that have not been updated to read the
 * `credit-card-convenience-fee-percent` LaunchDarkly flag.
 */
const DEFAULT_CREDIT_CARD_CONVENIENCE_FEE_MULTIPLIER = 0.035

/**
 * Calculate convenience fee for credit card payment.
 * Formula: `multiplier * paymentAmount`, rounded to the nearest cent.
 *
 * Callers should read the `credit-card-convenience-fee-percent` LaunchDarkly
 * flag (a percent, e.g. `3` for 3%) and convert to a multiplier with
 * `× 0.01` before passing it in. The default multiplier (`0.035`) preserves
 * the historical 3.5% rate.
 *
 * @param paymentAmount - The amount to be paid with credit card in cents
 * @param multiplier - The convenience fee multiplier (e.g. `0.035` for 3.5%)
 * @returns The convenience fee amount in cents
 */
export const calculateCreditCardConvenienceFee = (
  paymentAmount: number,
  multiplier: number = DEFAULT_CREDIT_CARD_CONVENIENCE_FEE_MULTIPLIER
): number => {
  if (paymentAmount <= 0) return 0
  return Math.round(multiplier * paymentAmount)
}

/**
 * This function handles three payment scenarios:
 * 1. Return original props if pay-with-terms is not applicable
 * 2. Split payment: Use net terms for available credit + secondary method for remainder
 * 3. Net terms only: Use net terms to cover full order amount
 *
 * @param props - Original checkout form values
 * @param amountDue - Total amount due for the order in cents
 * @param balanceMethod - Available net terms payment method
 * @param enableSplitPayment - Whether split payment feature is enabled
 * @returns Modified props with balance method added if conditions are met
 */
export const prepareCheckoutProps = (
  props: CheckoutValues,
  amountDue: number,
  balanceMethod?: NetTermsPayMethod,
  enableSplitPayment: boolean = false
): CheckoutValues => {
  if (
    !shouldProcessNetTerms(props, amountDue, balanceMethod, enableSplitPayment)
  ) {
    return props
  }

  const secondaryPayMethod = getSecondaryPayMethod(props.selectedPaymentMethods)
  const isSplitPayment =
    secondaryPayMethod &&
    isNetTermsCoveredBySecondary(
      props.selectedPaymentMethods,
      enableSplitPayment
    )
  if (isSplitPayment) {
    return prepareSplitPayment(
      props,
      amountDue,
      balanceMethod,
      secondaryPayMethod
    )
  }

  return prepareNetTermsOnly(props, amountDue, balanceMethod)
}

function shouldProcessNetTerms(
  props: CheckoutValues,
  amountDue: number,
  balanceMethod: NetTermsPayMethod | undefined,
  enableSplitPayment: boolean
): balanceMethod is NetTermsPayMethod {
  if (props.selected !== PAY_WITH_TERMS || !balanceMethod) {
    return false
  }

  const canCoverFullAmount = canPayWithNetTerms(amountDue, balanceMethod)
  const hasSplitPaymentSetup = isNetTermsCoveredBySecondary(
    props.selectedPaymentMethods,
    enableSplitPayment
  )

  return canCoverFullAmount || hasSplitPaymentSetup
}

function prepareSplitPayment(
  props: CheckoutValues,
  amountDue: number,
  balanceMethod: NetTermsPayMethod,
  secondaryPayMethod: PayMethod & { amount: number }
): CheckoutValues {
  const availableCredit =
    balanceMethod.details.creditInfo?.availableCreditInCents ?? 0

  const netTermsAmountUsed = Math.max(
    0,
    calculateNetTermsAmountUsed(availableCredit, amountDue)
  )

  const remainingAmount = amountDue - netTermsAmountUsed
  const secondaryPayment: PayMethod & { amount: number } = {
    ...secondaryPayMethod,
    amount: remainingAmount,
  }

  if (netTermsAmountUsed <= 0) {
    return {
      ...props,
      selectedPaymentMethods: [secondaryPayment],
      selected: secondaryPayment.id,
    }
  }

  const netTermsPayment: PayMethod & { amount: number } = {
    ...(balanceMethod as PayMethod),
    amount: netTermsAmountUsed,
  }

  return {
    ...props,
    selectedPaymentMethods: [netTermsPayment, secondaryPayment],
    selected: netTermsPayment.id,
  }
}

function prepareNetTermsOnly(
  props: CheckoutValues,
  amountDue: number,
  balanceMethod: NetTermsPayMethod
): CheckoutValues {
  return {
    ...props,
    selectedPaymentMethods: [
      {
        ...balanceMethod,
        amount: amountDue,
      },
    ],
    selected: balanceMethod.id,
  }
}

/**
 * Derives the list of pending convenience fees based on the selected payment method(s).
 *
 * @param values - Current Formik checkout values
 * @param enableSplitPayment - Whether the split payment feature flag is enabled
 * @param netTermsAmountUsed - The portion of the order covered by net terms (in cents)
 * @param amountDue - Total order amount due (in cents)
 * @param creditCardConvenienceFeeMultiplier - The credit card convenience fee
 *   multiplier (e.g. `0.035` for 3.5%). Defaults to `0.035` to preserve the
 *   historical rate for callers that have not yet been wired to the
 *   `credit-card-convenience-fee-percent` LaunchDarkly flag.
 */
export function computeConvenienceFees({
  values,
  enableSplitPayment,
  netTermsAmountUsed,
  amountDue,
  creditCardConvenienceFeeMultiplier = DEFAULT_CREDIT_CARD_CONVENIENCE_FEE_MULTIPLIER,
}: {
  values: CheckoutValues
  enableSplitPayment: boolean
  netTermsAmountUsed: number
  amountDue: number
  creditCardConvenienceFeeMultiplier?: number
}): ConvenienceFeeItem[] {
  const fees: ConvenienceFeeItem[] = []

  if (values.selected === PAY_WITH_TERMS) {
    const netTermsFee = calculateConvenienceFee(netTermsAmountUsed)
    if (netTermsFee > 0) {
      fees.push({
        type: 'netTerms',
        amount: netTermsFee,
      })
    }

    const secondaryMethod = getSecondaryPayMethod(values.selectedPaymentMethods)
    const isSplitWithCreditCard =
      secondaryMethod !== null &&
      isNetTermsCoveredBySecondary(
        values.selectedPaymentMethods,
        enableSplitPayment
      ) &&
      (secondaryMethod.type === PayMethodTypeEnum.Card ||
        secondaryMethod.type === PayMethodTypeEnum.GetbalanceCc)

    if (isSplitWithCreditCard) {
      const creditCardAmount = amountDue - netTermsAmountUsed
      if (creditCardAmount > 0) {
        fees.push({
          type: 'creditCard',
          amount: calculateCreditCardConvenienceFee(
            creditCardAmount,
            creditCardConvenienceFeeMultiplier
          ),
        })
      }
    }
  } else if (
    isCreditCardPaymentMethod(values.paymentMethods, values.selected)
  ) {
    const creditCardFee = calculateCreditCardConvenienceFee(
      amountDue,
      creditCardConvenienceFeeMultiplier
    )
    if (creditCardFee > 0) {
      fees.push({
        type: 'creditCard',
        amount: creditCardFee,
      })
    }
  }

  return fees
}
